import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellOff } from '@/components/icons';
import type { Notification } from '@senviro/core-api-client';
import { Button } from '@/components/ui/button';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { useMarkRead } from '@/features/notifications/api/use-mark-read';

export interface NotificationsDropdownProps {
  items: Notification[];
  isLoading: boolean;
  onClose?: () => void;
}

/**
 * NotificationsDropdown — entity-grouped list shown when the bell is clicked.
 *
 * W6-F5-S2 introduced the flat shell; W16-F2-S2 layers entity-type
 * grouping over it (legacy parity with `Element/Metronic/topBar.ctp`).
 *
 * Group order is fixed (not data-sorted): tasks first because they're
 * action-required, then invitations, then entities by hierarchy, then
 * a generic "system" bucket. W22-F7 will fill in per-event-type
 * rendering (e.g. invitation accept/decline buttons) inside the
 * `case 'invitation':` slot below.
 */

type GroupKey = 'task' | 'invitation' | 'asset' | 'portfolio' | 'holder' | 'partner' | 'system';

const GROUP_ORDER: readonly GroupKey[] = [
  'task',
  'invitation',
  'asset',
  'portfolio',
  'holder',
  'partner',
  'system',
];

/**
 * Map a notification to its group bucket. Today the data layer feeds
 * `entity_type` directly from Core (`asset_holder`, `portfolio`, etc.);
 * for kinds without a clear entity (`subscription.created`, generic
 * `notification.created`) we fall back to "system" so they still appear.
 *
 * TODO(W22-F7): when Core ships invitation events, route them to the
 * `invitation` bucket via `kind === 'invitation.received'` or similar.
 */
function groupForNotification(n: Notification): GroupKey {
  const entity = (n.entity_type ?? '').toLowerCase();
  const kind = (n.kind ?? '').toLowerCase();
  if (kind.startsWith('task.')) return 'task';
  if (kind.startsWith('invitation.')) return 'invitation';
  if (entity === 'asset' || entity === 'building') return 'asset';
  if (entity === 'portfolio') return 'portfolio';
  if (entity === 'asset_holder' || entity === 'holder') return 'holder';
  if (entity === 'partner' || entity === 'organization') return 'partner';
  if (entity === 'service_issue' || kind.startsWith('task.')) return 'task';
  return 'system';
}

export function NotificationsDropdown({
  items,
  isLoading,
  onClose,
}: NotificationsDropdownProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const markRead = useMarkRead();

  function handleSelect(n: Notification): void {
    if (n.read_at == null) markRead.mutate(n.id);
    const extra = n.extra_data as { entity_url?: unknown } | undefined;
    if (typeof extra?.entity_url === 'string') {
      onClose?.();
      navigate(extra.entity_url);
    }
  }

  function handleViewAll(): void {
    onClose?.();
    navigate('/notifications');
  }

  // Group items, preserving original order within each bucket.
  const grouped = React.useMemo(() => {
    const out: Record<GroupKey, Notification[]> = {
      task: [],
      invitation: [],
      asset: [],
      portfolio: [],
      holder: [],
      partner: [],
      system: [],
    };
    for (const n of items.slice(0, 10)) {
      out[groupForNotification(n)].push(n);
    }
    return out;
  }, [items]);

  const hasAny = Object.values(grouped).some((bucket) => bucket.length > 0);

  return (
    <div
      role="region"
      aria-label={t('notifications.heading')}
      data-testid="notifications-dropdown"
      className="flex w-80 max-w-[90vw] flex-col"
    >
      <header className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-sm font-semibold">{t('notifications.heading')}</span>
      </header>

      <div className="max-h-[60vh] overflow-y-auto px-1 py-1">
        {isLoading ? (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">
            {t('notifications.loading')}
          </p>
        ) : !hasAny ? (
          <div
            className="flex flex-col items-center gap-2 px-3 py-8 text-center"
            data-testid="dropdown-empty"
          >
            <BellOff className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            <p className="text-xs text-muted-foreground">
              {t('notifications.empty')}
            </p>
          </div>
        ) : (
          GROUP_ORDER.filter((g) => grouped[g].length > 0).map((g) => (
            <section key={g} aria-labelledby={`notif-group-${g}`}>
              <h4
                id={`notif-group-${g}`}
                className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {t(`notifications.groups.${g}` as 'notifications.groups.task')}
              </h4>
              {grouped[g].map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onSelect={handleSelect}
                  onMarkRead={(id) => markRead.mutate(id)}
                />
              ))}
            </section>
          ))
        )}
      </div>

      <footer className="border-t border-border px-2 py-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-center text-xs"
          onClick={handleViewAll}
          data-testid="see-all"
        >
          {t('notifications.viewAll')}
        </Button>
      </footer>
    </div>
  );
}
